/**
 * CompletionShareSheet — viral trigger that fires exactly once when
 * the guest completes ALL experiences. NOT a toast (too weak for this
 * peak-emotional-investment moment) — a deliberate bottom sheet that
 * stacks: completion → recognition → purpose → action.
 *
 * One-shot per guest per event (localStorage `event_shared_prompt_${slug}`).
 * 400ms delay so the dopamine of the last completion lands before the
 * share ask. Soft secondary "Not now" — never blocks the page.
 *
 * Social-proof copy in the share text (`${completed} experiences
 * already unlocked`) is the highest-leverage detail here: it turns a
 * generic "join" link into evidence that the event is alive.
 */
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { ShareNetwork, X, Sparkle } from '@phosphor-icons/react';
import { completionStats } from '../lib/features';

const SHARE_KEY = (slug) => `event_shared_prompt_${slug}`;

export default function CompletionShareSheet({ slug, features, eventName, eventId }) {
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const scheduledRef = useRef(false);

  // Re-evaluate completion stats on every tick. Storage events fire
  // when other tabs / the feature pages write completions; the
  // eventdrop:completion_tick custom event covers same-tab writes.
  useEffect(() => {
    const onStorage = () => setTick((t) => t + 1);
    const onComplete = () => setTick((t) => t + 1);
    window.addEventListener('storage', onStorage);
    window.addEventListener('eventdrop:completion_tick', onComplete);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('eventdrop:completion_tick', onComplete);
    };
  }, []);

  useEffect(() => {
    void tick;
    if (!slug || !features || features.length <= 1) return;
    if (scheduledRef.current) return;       // already armed/fired in this mount
    const stats = completionStats(slug, features);
    if (stats.total <= 1) return;
    if (stats.completed < stats.total) return;
    let alreadyShown = false;
    try { alreadyShown = !!localStorage.getItem(SHARE_KEY(slug)); } catch {}
    if (alreadyShown) return;
    scheduledRef.current = true;
    // 400ms delay so the last micro-celebration toast resolves first.
    // We deliberately do NOT return a cleanup that clears the timer —
    // a parent re-render would otherwise kill the timer before it
    // fires. The scheduledRef guard already prevents double-scheduling.
    setTimeout(() => {
      try { localStorage.setItem(SHARE_KEY(slug), '1'); } catch {}
      setOpen(true);
    }, 400);
  }, [tick, slug, features]);

  const stats = features ? completionStats(slug, features) : { completed: 0, total: 0 };

  const handleShare = async () => {
    const url = window.location.href;
    // Social proof: bake the unlock count into the share text so the
    // recipient sees evidence the event is alive, not a cold link.
    const text = `Join ${eventName || 'this event'} — ${stats.completed} experiences already unlocked 🎉`;
    let succeeded = false;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Join this event', text, url });
        toast.success("🙌 You're helping make this event unforgettable", { duration: 2400 });
        succeeded = true;
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied — share it anywhere');
        succeeded = true;
      } else {
        toast.success('Share this link with your friends');
        succeeded = true;
      }
    } catch (e) {
      // user cancelled the native share sheet — silent
      if (e?.name !== 'AbortError') toast.error('Could not share — try copying the link');
    }
    // Telemetry: only on actual success (not on user-cancelled native
    // share). Fire-and-forget — never block the UX. ref=guest because
    // this sheet only fires for completed-everything guests.
    if (succeeded && eventId) {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        // Lazy import axios to avoid pulling it into the bundle for
        // this tiny call (the parent already loads it).
        const ax = (await import('axios')).default;
        ax.post(`${BACKEND_URL}/api/events/${eventId}/track-share`, { ref: 'guest' }).catch(() => {});
      } catch {}
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="completion-share-sheet"
    >
      <div
        className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-6 bg-[#15161B] border border-white/10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white"
          aria-label="Dismiss"
          data-testid="completion-share-close"
        >
          <X size={12} weight="bold" />
        </button>

        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}>
            <Sparkle size={28} weight="fill" className="text-white" />
          </div>
          <h2 className="text-xl font-black text-white mb-1.5">🎊 You unlocked everything!</h2>
          <p className="text-sm text-white/60 leading-relaxed">
            You've helped capture amazing moments for this event. Help the hosts even more by sharing this with other guests 👇
          </p>
        </div>

        <button
          onClick={handleShare}
          className="w-full py-3.5 rounded-xl text-sm font-black text-white flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', boxShadow: '0 12px 36px rgba(194,157,82,0.18)' }}
          data-testid="completion-share-primary"
        >
          <ShareNetwork size={16} weight="bold" />
          Share with guests
        </button>

        <p className="text-[10px] text-center text-white/35 mt-3" data-testid="completion-share-microcopy">
          Most shared events get 2× more memories
        </p>

        <button
          onClick={() => setOpen(false)}
          className="w-full mt-3 text-xs font-bold text-white/45 hover:text-white/80 py-2"
          data-testid="completion-share-dismiss"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
